import React, { useEffect, useMemo, useState } from "react";
import ScreeningAbstractPanel from "./ScreeningAbstractPanel";
import axios from "axios";
import GetCookie from "../../../../utils/GetCookie";

import ScreeningPDFPanel from "./Screeningpdfpanel";
import { PICO_COLORS, getPicoTypeForTag, normalizePicoJson } from "../../../../utils/Picocolors";

const ReasonRow = ({ reason, checked, onToggle, disabled = false }) => {
  const isInclude = (reason?.type || "").toLowerCase() === "include";

  return (
    <div className="d-flex align-items-center gap-1 mb-1">
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={!!checked}
        onChange={() => onToggle?.(reason)}
        disabled={disabled}
        className="w-5"
      />

      {/* Include / Exclude pill */}
      <div
        className={`d-flex align-items-center gap-1 px-2 py-1 ev-radius-xsmall ev-bold-med
          ${isInclude ? "slr-criteria-incl" : "slr-criteria-excl"}`}
        style={{ minWidth: 90, justifyContent: "center" }}
      >
        <span
          className="d-flex align-items-center justify-content-center ev-radius-large ev-bold-large"
          style={{
            width: 16,
            height: 16,
            background: isInclude ? "#4CAF50" : "#E53935",
            color: "#fff",
            fontSize: 11,
          }}
        >
          {isInclude ? "✓" : "✕"}
        </span>

        <span className="ev-reg-p-dark">{isInclude ? "Include" : "Exclude"}</span>
      </div>

      {/* RIGHT: Reason text box */}
      <textarea
        rows={1}
        readOnly
        value={reason.label || ""}
        className="formField3 flex-grow-1 ev-card-8 p-1 ev-radius-xsmall ev-small-p-dark-blue ev-bold-reg"
      />
    </div>
  );
};

const OpenReviewPanel = ({
  onClose,
  conflictMode = false,
  isQC = false,
  isFullText = false,
  fullTextScreeningId,
  abstractId,
  slrFabricId,
  onNavigationUpdate,
  onSaveSuccess,
  onSaveHandlerReady,
  onDataChange,
  pico_json,
  activeFilters = {},
  // Set by Screening.jsx when the stage's QC is complete and the user is not
  // the project admin: decisions are visible but cannot be changed.
  readOnly: qcLocked = false,
  // DUAL_HUMAN_HUMAN only: which set the creator is screening as ("SET_1" /
  // "SET_2"). null for reviewers and for every other framework — the backend
  // then derives the set from the framework itself.
  reviewerSet = null,
}) => {
  /* --- STATE --- */
  const [selectedReasonIds, setSelectedReasonIds] = useState([]);
  const [manualReasons, setManualReasons] = useState([]); // [{ id, reason_text, is_manual }]
  const [comments, setComments] = useState("");
  const [decision, setDecision] = useState("EXCLUDED"); // default
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [isDuplicateExclusion, setIsDuplicateExclusion] = useState(false);
  // Project owner who is not an allocated reviewer: the backend lets them open
  // every study and ask the AI Assistant, but has no allocation row for a
  // decision to hang off, so every write path would be refused. The panel
  // renders the same content with the inputs inert rather than offering
  // controls that always fail.
  const [serverReadOnly, setReadOnly] = useState(false);
  const readOnly = serverReadOnly || qcLocked;

  // Arrays to hold multiple decisions
  const [humanDecisions, setHumanDecisions] = useState([]);
  const [aiDecisions, setAiDecisions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [literature, setLiterature] = useState(null);
  const [criteriaList, setCriteriaList] = useState([]);
  const [navData, setNavData] = useState({
    next_id: null,
    previous_id: null,
    current_index: 0,
    total: 0,
  });
  const [saving, setSaving] = useState(false);

  // ═══ AI Assistant State ═══
  const [aiAssistEnabled, setAiAssistEnabled] = useState(false);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistSuggestion, setAiAssistSuggestion] = useState(null);
  const [aiAssistError, setAiAssistError] = useState(null);
  // Which of the AI's suggested reasons the reviewer picked. Applying all of
  // them automatically made the reviewer deselect the ones they disagreed
  // with, so nothing is pre-ticked and they choose what actually applies.
  const [selectedAssistReasonIds, setSelectedAssistReasonIds] = useState([]);

  const toggleAssistReason = (id) => {
    setSelectedAssistReasonIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };
  // Whether an allocated reviewer has already recorded a decision on this
  // study. In read-only mode the decision form is only worth showing once
  // there is something to read — an empty form defaulted to "Exclude" looks
  // like a verdict nobody made.

  // A new suggestion invalidates the previous picks.
  useEffect(() => {
    setSelectedAssistReasonIds([]);
  }, [aiAssistSuggestion]);

  // Save handlers now throw on error so parent (Screening.jsx) shows a single popup
  const showError = (msg) => {
    throw new Error(msg || "Something went wrong");
  };

  const API_BASE_URL = "/pceapi/slr";

  // useMemo for headers (Eavior pattern)
  const headers = useMemo(
    () => ({
      Authorization: GetCookie("access_token"),
    }),
    [],
  );

  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        decision: decision,
        criteria_ids: selectedReasonIds,
        comments: comments,
        tags: [],
      });
    }
  }, [decision, selectedReasonIds, comments, onDataChange]);

  useEffect(() => {
    if (!slrFabricId) return;

    if (isFullText && !fullTextScreeningId) return;
    if (!isFullText && !abstractId) return;

    setDecision("EXCLUDED");
    setSelectedReasonIds([]);
    setManualReasons([]);
    setComments("");
    setDecisionOpen(false);
    setIsDuplicateExclusion(false);

    // Reset AI assist state on card change
    setAiAssistEnabled(false);
    setAiAssistSuggestion(null);
    setAiAssistError(null);

    const fetchDetails = async () => {
      setLoading(true);
      try {
        // --- 1. DETERMINE ENDPOINT BASED ON MODE ---
        let endpoint = "";
        if (isQC) {
          endpoint = isFullText
            ? "/pceapi/slr/full-text-screening-qc/detail"
            : "/pceapi/slr/abstract-screening-qc/detail";
        } else if (isFullText) {
          endpoint = "/pceapi/slr/full-text-screening-human/detail";
        } else {
          endpoint = "/pceapi/slr/abstract-screening-human/detail";
        }

        // --- 2. CONSTRUCT PAYLOAD ---
        // QC and conflict endpoints expect `decision`, human endpoints expect `decision_filter`
        const filterPayload = isQC
          ? {
              decision: activeFilters.decision_filter ?? null,
              search: activeFilters.search ?? null,
              start_year: activeFilters.start_year ?? null,
              end_year: activeFilters.end_year ?? null,
              selected_tags: activeFilters.selected_tags ?? null,
              confidence_start: activeFilters.confidence_start ?? null,
              confidence_end: activeFilters.confidence_end ?? null,
            }
          : {
              decision_filter: activeFilters.decision_filter ?? null,
              search: activeFilters.search ?? null,
              start_year: activeFilters.start_year ?? null,
              end_year: activeFilters.end_year ?? null,
              selected_tags: activeFilters.selected_tags ?? null,
            };

        const payload = { slr_fabric_id: slrFabricId, ...filterPayload };
        if (isFullText) {
          payload.full_text_screening_id = fullTextScreeningId;
        } else {
          payload.abstract_screening_id = abstractId;
        }

        const res = await axios.post(endpoint, payload, { headers });

        if (res.data?.status === "success") {
          // All responses now have nested structure: { status, data: { study, ... } }
          const data = res.data.data || res.data;

          // --- 3. HANDLE LITERATURE DATA ---
          let studyData = null;
          if (isQC) {
            studyData = isFullText
              ? { ...data.study, ...data.document }
              : data.study;
          } else if (isFullText) {
            studyData = data.full_text_screening || data.document || data.study;
          } else {
            // Abstract screening - backend returns 'study' not 'abstract'
            studyData = data.study || data.abstract;
          }

          if (!studyData && data.title) studyData = data;

          setReadOnly(Boolean(data.read_only));

          const mergedLiterature = isFullText
            ? { ...data.study, ...data.document }
            : data.study;

          setLiterature({
            ...mergedLiterature,
            pico_json: pico_json || {},
          });

          // --- 4. HANDLE CRITERIA (Common for Abstract & FT usually) ---
          if (!isQC && data.available_criteria) {
            const mappedCriteria = data.available_criteria.map((c) => ({
              id: c.id,
              label: c.reason_code || c.reason,
              type: c.criteria === "INCLUSION" ? "include" : "exclude",
            }));
            setCriteriaList(mappedCriteria);
          }

          // QC Logic (kept same)
          if (isQC && data.available_reasons) {
            const incl = (data.available_reasons.inclusion || []).map((r) => ({
              id: r.id,
              label: r.reason_code || r.reason,
              type: "include",
            }));
            const excl = (data.available_reasons.exclusion || []).map((r) => ({
              id: r.id,
              label: r.reason_code || r.reason,
              type: "exclude",
            }));
            setCriteriaList([...incl, ...excl]);
          }

          // --- 5. HANDLE PRE-EXISTING DECISION ---
          if (!isQC && data.decision) {
            setDecision(data.decision.human_decision || "EXCLUDED");
            setComments(data.decision.human_comments || "");
            // Keep only reasons that still exist in the project's criteria.
            // A removed/replaced criterion stayed selected but invisible, so
            // "at least one reason" passed with nothing ticked on screen.
            const availableIds = new Set((data.available_criteria || []).map((c) => c.id));
            const savedIds = data.decision.criteria_ids || [];
            setSelectedReasonIds(
              data.available_criteria ? savedIds.filter((id) => availableIds.has(id)) : savedIds
            );
            setManualReasons(data.decision.manual_reasons || []);
            setIsDuplicateExclusion((data.decision.human_comments || "").toLowerCase().includes("duplicate entry"));
          } else if (!isQC) {
            setDecision("EXCLUDED");
            setComments("");
            setSelectedReasonIds([]);
            setManualReasons([]);
          }          
          
          if (isQC && data.final_decision) {
            const finalDecision = data.final_decision;

            setDecision(finalDecision.decision || "EXCLUDED");
            setComments(finalDecision.comments || "");

            const finalReasons = finalDecision.reasons || [];
            if (finalReasons.length > 0) {
              setSelectedReasonIds(finalReasons.map((r) => r.id ?? r));
            } else {
              const humanReasons = (data.decision_history?.human?.[0]?.reasons || []);
              const aiReasons    = (data.decision_history?.ai?.[0]?.reasons    || []);
              const fallback     = humanReasons.length > 0 ? humanReasons : aiReasons;
              setSelectedReasonIds(fallback.map((r) => r.id ?? r));
            }
          } else if (isQC) {
            setDecision("EXCLUDED");
            setComments("");
            setSelectedReasonIds([]);
          }

          if (isQC) {
            const humanHistory = data.decision_history?.human || [];
            const aiHistory = data.decision_history?.ai || [];
            const isDup =
              humanHistory.some((h) => (h.comments || "").toLowerCase().includes("duplicate entry")) ||
              aiHistory.some((a) => (a.summary || a.comments || "").toLowerCase().includes("duplicate entry"));
            setIsDuplicateExclusion(isDup);
            setHumanDecisions(humanHistory);
            setAiDecisions(aiHistory);
          }

          if (data.navigation && onNavigationUpdate) {
            onNavigationUpdate({
              ...data.navigation,
              study_id: data.document?.study_id || data.navigation.study_id
            });
          }

          // --- 6. AI ASSIST (Human abstract, QC, and Conflict) ---
          if (data.ai_assist_enabled) {
            setAiAssistEnabled(true);
          }
        }
      } catch (err) {
        console.error(`Failed to fetch details`, err);
        showError(
          err.response?.data?.message || "Failed to load screening details",
        );
      } finally {
        setLoading(false);
      }
    };

    if (!conflictMode) {
      fetchDetails();
    }
  }, [
    abstractId,
    fullTextScreeningId,
    slrFabricId,
    isQC,
    conflictMode,
    isFullText,
    activeFilters,
  ]);

  // ═══ AI Assistant — trigger fetch when enabled flag is set by fetchDetails ═══
  useEffect(() => {
    const idReady = isFullText ? !!fullTextScreeningId : !!abstractId;
    if (aiAssistEnabled && idReady && slrFabricId) {
      fetchAiAssistSuggestion(false);
    }
  }, [aiAssistEnabled, abstractId, fullTextScreeningId, slrFabricId]);

  // ═══ AI Assistant — fetch suggestion ═══
  const fetchAiAssistSuggestion = async (forceRegenerate = false) => {
    // Full-text cards have no abstractId (Screening.jsx passes undefined), so
    // checking abstractId alone meant the full-text request was never sent.
    const idReady = isFullText ? !!fullTextScreeningId : !!abstractId;
    if (!idReady || !slrFabricId) return;

    setAiAssistLoading(true);
    setAiAssistError(null);
    try {
      const endpoint = isFullText
        ? `${API_BASE_URL}/full-text-screening-assist/suggestion`
        : `${API_BASE_URL}/abstract-screening-assist/suggestion`;
      const payload = isFullText
        ? { slr_fabric_id: slrFabricId, full_text_screening_id: fullTextScreeningId, force_regenerate: forceRegenerate }
        : { slr_fabric_id: slrFabricId, abstract_screening_id: abstractId, force_regenerate: forceRegenerate };
      const res = await axios.post(endpoint, payload, { headers });

      if (res.data?.status === "success" && res.data.data?.suggestion) {
        setAiAssistSuggestion(res.data.data.suggestion);
      } else if (res.data?.status === "failed") {
        setAiAssistError(res.data?.message || "Failed to load AI suggestion");
      }
    } catch (err) {
      console.error("AI Assist fetch failed:", err);
      setAiAssistError("Failed to load AI suggestion");
    } finally {
      setAiAssistLoading(false);
    }
  };

  // AI Assist — use suggestion to fill form.
  // Applies the suggested decision plus whichever reasons the reviewer ticked
  // above. With no suggested reasons there is nothing to pick, so the decision
  // alone is applied and the reason list is left untouched.
  const handleUseAssistSuggestion = () => {
    if (!aiAssistSuggestion || readOnly) return;

    const suggestedReasons = aiAssistSuggestion.reasons || [];
    if (suggestedReasons.length > 0 && selectedAssistReasonIds.length === 0) return;

    setDecision(aiAssistSuggestion.decision || "EXCLUDED");
    if (selectedAssistReasonIds.length > 0) {
      setSelectedReasonIds([...selectedAssistReasonIds]);
    }
    setComments("");
  };

  // ✅ UPDATED: Save Human Decision (Abstract & Full Text)
  const handleSaveHumanDecision = async () => {
    if (!slrFabricId) return;

    if (isFullText && !fullTextScreeningId) return;
    if (!isFullText && !abstractId) return;

    // For abstract screening INCLUDED, reasons are optional
    const skipReasonValidation = decision === "INCLUDED" && !isFullText;

    if (!skipReasonValidation && selectedReasonIds.length === 0) {
      showError(
        `Please select at least one reason for ${decision === "INCLUDED" ? "inclusion" : "exclusion"}`,
      );
      return;
    }

    if (!skipReasonValidation) {
      const hasMismatchedReasons = selectedReasonIds.some((id) => {
        const reason = criteriaList.find((c) => c.id === id);
        if (!reason) return false;
        return decision === "INCLUDED" ? reason.type !== "include" : reason.type !== "exclude";
      });

      if (hasMismatchedReasons) {
        showError(
          `Selected reasons do not match the final decision. Please select only ${decision === "INCLUDED" ? "inclusion" : "exclusion"} reasons.`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      // Determine endpoint
      const endpoint = isFullText
        ? `${API_BASE_URL}/full-text-screening-human/save-decision`
        : `${API_BASE_URL}/abstract-screening-human/save-decision`;

      // Base Payload
      const payload = {
        slr_fabric_id: slrFabricId,
        decision: decision,
        criteria_ids: selectedReasonIds,
        comments: comments || null,
        reviewer_set: reviewerSet,
      };

      // Specific fields based on mode
      if (isFullText) {
        payload.full_text_screening_id = fullTextScreeningId;
        // Full text API does not expect 'tags'
      } else {
        payload.abstract_screening_id = abstractId;
        payload.selected_tags = [];
      }

      const res = await axios.post(endpoint, payload, { headers });

      if (res.data?.status === "success") {
        if (onSaveSuccess) onSaveSuccess();

        if (navData?.next_id && onNavigationUpdate) {
          onNavigationUpdate({
            ...navData,
            current_index: navData.current_index + 1,
          });
        }
      } else {
        showError(res.data?.message || "Failed to save decision");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to save decision");
    } finally {
      setSaving(false);
    }
  };

  // ✅ UPDATED: Resolve Conflict (Abstract & Full Text)
  const handleResolveConflict = async () => {
    if (!slrFabricId) return;

    if (isFullText && !fullTextScreeningId) return;
    if (!isFullText && !abstractId) return;

    // Reasons are only required when resolving as EXCLUDED
    const skipReasonValidation = decision === "INCLUDED";

    if (!skipReasonValidation && selectedReasonIds.length === 0) {
      showError(
        `Please select at least one reason for ${decision === "INCLUDED" ? "inclusion" : "exclusion"}`,
      );
      return;
    }

    if (!skipReasonValidation) {
      const hasMismatchedReasons = selectedReasonIds.some((id) => {
        const reason = criteriaList.find((c) => c.id === id);
        if (!reason) return false;
        return decision === "INCLUDED" ? reason.type !== "include" : reason.type !== "exclude";
      });

      if (hasMismatchedReasons) {
        showError(
          `Selected reasons do not match the final decision. Please select only ${decision === "INCLUDED" ? "inclusion" : "exclusion"} reasons.`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      const endpoint = isFullText
        ? `${API_BASE_URL}/full-text-screening-conflicts/resolve`
        : `${API_BASE_URL}/abstract-screening-conflicts/resolve`;

      const payload = {
        slr_fabric_id: slrFabricId,
        decision: decision, // "INCLUDED" or "EXCLUDED"
        reason_ids: selectedReasonIds,
        comments: comments || null,
      };

      if (isFullText) {
        payload.full_text_screening_id = fullTextScreeningId;
      } else {
        payload.abstract_screening_id = abstractId;
      }

      const res = await axios.post(endpoint, payload, { headers });

      if (res.data?.status === "success") {
        if (onSaveSuccess) onSaveSuccess();

        if (navData?.next_id) {
          if (onNavigationUpdate) {
            onNavigationUpdate({
              ...navData,
              current_index: navData.current_index + 1,
            });
          }
        }
      } else {
        showError(res.data?.message || "Failed to resolve conflict");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to resolve conflict");
    } finally {
      setSaving(false);
    }
  };

  // ✅ UPDATED: Save QC Decision (Abstract & Full Text)
  const handleSaveQCDecision = async () => {
    if (!slrFabricId) return;

    if (isFullText && !fullTextScreeningId) return;
    if (!isFullText && !abstractId) return;

    if (selectedReasonIds.length === 0) {
      showError(
        `Please select at least one reason for ${decision === "INCLUDED" ? "inclusion" : "exclusion"}`,
      );
      return;
    }

    const hasMismatchedReasons = selectedReasonIds.some((id) => {
      const reason = criteriaList.find((c) => c.id === id);
      if (!reason) return false;
      return decision === "INCLUDED" ? reason.type !== "include" : reason.type !== "exclude";
    });

    if (hasMismatchedReasons) {
      showError(
        `Selected reasons do not match the final decision. Please select only ${decision === "INCLUDED" ? "inclusion" : "exclusion"} reasons.`,
      );
      return;
    }

    setSaving(true);
    try {
      const endpoint = isFullText
        ? `${API_BASE_URL}/full-text-screening-qc/update`
        : `${API_BASE_URL}/abstract-screening-qc/update`;

      const payload = {
        slr_fabric_id: slrFabricId,
        decision: decision,
        reason_ids: selectedReasonIds,
        comments: comments || null,
      };

      if (isFullText) {
        payload.full_text_screening_id = fullTextScreeningId;
      } else {
        payload.abstract_screening_id = abstractId;
      }

      const res = await axios.post(endpoint, payload, { headers });

      if (res.data?.status === "success") {
        // Success — parent (Screening.jsx) shows the popup

        if (onSaveSuccess) onSaveSuccess();

        if (navData?.next_id && onNavigationUpdate) {
          onNavigationUpdate({
            ...navData,
            current_index: (navData.current_index || 0) + 1,
          });
        }
      } else {
        showError(res.data?.message || "Failed to save QC decision");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to save QC decision";
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ✅ UPDATED: Fetch Conflict Details (Abstract & Full Text)
  const fetchConflictDetail = async () => {
    if (!slrFabricId || !conflictMode) return;
    if (isFullText && !fullTextScreeningId) return;
    if (!isFullText && !abstractId) return;

    setLoading(true);
    try {
      const endpoint = isFullText
        ? `${API_BASE_URL}/full-text-screening-conflicts/detail`
        : `${API_BASE_URL}/abstract-screening-conflicts/detail`;

      // Conflict endpoints expect `decision` (not `decision_filter`)
      const filterPayload = {
        decision: activeFilters.decision_filter ?? null,
        search: activeFilters.search ?? null,
        start_year: activeFilters.start_year ?? null,
        end_year: activeFilters.end_year ?? null,
        selected_tags: activeFilters.selected_tags ?? null,
        confidence_start: activeFilters.confidence_start ?? null,
        confidence_end: activeFilters.confidence_end ?? null,
      };

      const payload = { slr_fabric_id: slrFabricId, ...filterPayload };

      if (isFullText) {
        payload.full_text_screening_id = fullTextScreeningId;
      } else {
        payload.abstract_screening_id = abstractId;
      }

      const res = await axios.post(endpoint, payload, { headers });

      if (res.data?.status === "success") {
        // Response structure: { status, data: { study, human_decisions, ai_decisions, ... } }
        const data = res.data.data || res.data;

        setLiterature({
          ...(isFullText ? data.document : data.study),
          pico_json: pico_json || {},
        });

        // ✅ Store ALL decisions as arrays
        setHumanDecisions(data.human_decisions || []);
        setAiDecisions(data.ai_decisions || []);

        // ✅ Reasons mapping (available_reasons: inclusion/exclusion)
        const incl = (data.available_reasons?.inclusion || []).map((r) => ({
          id: r.id,
          label: r.reason_code || r.reason,
          type: "include",
        }));

        const excl = (data.available_reasons?.exclusion || []).map((r) => ({
          id: r.id,
          label: r.reason_code || r.reason,
          type: "exclude",
        }));

        setCriteriaList([...incl, ...excl]);

        // ✅ Existing conflict resolution (prefill)
        const cr = data.conflict_resolution || {};
        setDecision(cr.decision || "EXCLUDED");
        setComments(cr.comments || "");
        setSelectedReasonIds((cr.reasons || []).map((x) => x.id));

        // ✅ Navigation
        if (data.navigation && onNavigationUpdate) {
          const navWithStudy = {
            ...data.navigation,
            study_id: data.document?.study_id || data.navigation.study_id
          };
          setNavData(navWithStudy);
          onNavigationUpdate(navWithStudy);
        }

        // ✅ AI ASSIST (Conflict Resolution)
        if (data.ai_assist_enabled) {
          setAiAssistEnabled(true);
        }
      }
    } catch (err) {
      console.error("Conflict detail fetch error:", err);
      showError(
        err.response?.data?.message || "Failed to load conflict details",
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper to auto-fill form from a previous decision
  const handleUseDecision = (sourceData) => {
    setDecision(sourceData.decision || "EXCLUDED");

    // Handle Reasons: Check if they are objects with IDs or just IDs
    const reasons = sourceData.reasons || [];
    const reasonIds = reasons.map((r) => (typeof r === "object" ? r.id : r));
    setSelectedReasonIds(reasonIds);

    // AI uses 'summary', Human uses 'comments'
    setComments(sourceData.summary || sourceData.comments || "");
  };

  const handleDecision = (type) => {
    setDecision(type);
    // Don't clear reasons - user needs to select reasons for both INCLUDED and EXCLUDED
  };

  const toggleReason = (id) => {
    if (readOnly) return;
    setSelectedReasonIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const PicoTag = ({ text, picoJson }) => {
    const picoType = getPicoTypeForTag(text, picoJson);
    const color = PICO_COLORS[picoType];

    return (
      <span
        className="ev-btn-xsmall ev-bold-reg py-1 px-2 ev-radius-large"
        style={{
          background: color ? `${color}22` : "#f2f3f8",
          color: color || "#333",
        }}
      >
        {text}
      </span>
    );
  };

  const matchingTags = useMemo(() => {
    const picoJson = literature?.pico_json || pico_json || {};
    const normalized = normalizePicoJson(picoJson);
    const title = (literature?.title || "").toLowerCase();
    const abstract = (literature?.abstract || literature?.description || "").toLowerCase();
    const text = `${title} ${abstract}`;

    if (!text.trim()) return [];

    const allKeywords = Object.values(normalized)
      .filter(Array.isArray)
      .flat()
      .map((k) => (k == null ? "" : String(k)).trim())
      .filter(Boolean);

    return [...new Set(allKeywords.filter((kw) => text.includes(kw.toLowerCase())))];
  }, [literature, pico_json]);

  useEffect(() => {
    if (conflictMode) {
      setSelectedReasonIds([]);
      setComments("");
      setDecision("EXCLUDED");
      setReadOnly(false);
      fetchConflictDetail();
    }
  }, [abstractId, fullTextScreeningId, slrFabricId, conflictMode, isFullText, activeFilters]);

  useEffect(() => {
    if (!onSaveHandlerReady) return;

    // Registering nothing leaves canSave false in Screening.jsx, which hides
    // the Save button. Offering one that the server always rejects would be
    // worse than not showing it.
    if (readOnly) {
      onSaveHandlerReady(null);
      return;
    }

    if (conflictMode) {
      onSaveHandlerReady(handleResolveConflict);
    } else if (isQC) {
      onSaveHandlerReady(handleSaveQCDecision);
    } else {
      onSaveHandlerReady(handleSaveHumanDecision);
    }
  }, [
    readOnly,
    conflictMode,
    isQC,
    abstractId,
    fullTextScreeningId,
    slrFabricId,
    decision,
    selectedReasonIds,
    comments,
    isFullText,
  ]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column p-0 overflow-hidden">
      <div className="row g-0 overflow-hidden">
        {/* LEFT: STUDY CONTENT (Scrollable) */}
        <div className="col-md-8 pe-2">
          {isFullText ? (
            <ScreeningPDFPanel literature={literature} />
          ) : (
            // No "Full text" link in abstract screening — decide on the abstract only.
            <ScreeningAbstractPanel
              literature={literature ? { ...literature, full_text_link: null } : literature}
            />
          )}
        </div>

        {/* RIGHT: DECISION FORM (Scrollable) */}
        <div className="ev-card-2 col-md-4 vh-75 scrollable-element p-2 ev-radius-small">
          <div className="ev-card-3 p-2 mb-2 ev-radius-xsmall text-center">
            <p className="ev-bold-med ev-reg-p-dark-blue mb-0 text-center">
              {conflictMode
                ? "Resolve Conflict"
                : isQC
                  ? "Final Decision"
                  : "Reviewer Decision"}
            </p>
          </div>

          {readOnly && (
            <div className="ev-alert-card-warning ev-radius-xsmall mb-2 ev-fs-11">
              <i className={`bi ${qcLocked ? "bi-check-circle" : "bi-eye"} me-1`}></i>
              {qcLocked
                ? `${isFullText ? "Full-text" : "Abstract"} screening is completed`
                : "Read-only"}
            </div>
          )}

          {/* ═══ AI ASSISTANT SECTION (Human, QC, Conflict) ═══ */}
          {aiAssistEnabled && (
            <div className="ev-card-4 p-2 mb-2 ev-radius-xsmall">
              {/* Header row */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-1">
                  <i className="bi bi-stars gradient-font6 ev-fs-14"></i>
                  <span className="ev-bold-med ev-reg-p-dark-blue">AI Assistant</span>
                </div>
                <span className="ev-message-card5 ev-radius-large ev-btn-xsmall px-2 py-1 ev-xsmall-p-dark ev-bold-med">
                  Enabled
                </span>
              </div>

              {aiAssistLoading ? (
                <div className="d-flex justify-content-center align-items-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ev-xsmall-p-dark ms-2">Generating suggestion...</span>
                </div>
              ) : aiAssistError ? (
                <div className="ev-alert-card-warning ev-radius-xsmall p-2 mb-1 ev-xsmall-p-dark">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {aiAssistError}
                  <button
                    className="ev-btn-blue-outline-round ev-btn-xsmall ms-2"
                    onClick={() => fetchAiAssistSuggestion(false)}
                  >
                    Retry
                  </button>
                </div>
              ) : aiAssistSuggestion ? (
                <>
                  {/* Suggested Decision */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="ev-small-p-blue ev-bold-med">AI Suggested Decision</span>
                    <div
                      className={`ev-decision-card ${aiAssistSuggestion.decision === "INCLUDED" ? "included" : "excluded"}`}
                    >
                      <span
                        className={`ev-decision-icon ${aiAssistSuggestion.decision === "INCLUDED" ? "included" : "excluded"}`}
                      >
                        <i
                          className={`bi ${aiAssistSuggestion.decision === "INCLUDED" ? "bi-check-lg" : "bi-x-lg"}`}
                        />
                      </span>
                      {aiAssistSuggestion.decision === "INCLUDED" ? "Include" : "Exclude"}
                    </div>
                  </div>

                  {/* Why AI suggests this */}
                  {aiAssistSuggestion.reasons?.length > 0 && (
                    <div className="mb-2">
                      <p className="ev-xsmall-p-dark ev-bold-med mb-1">Why AI suggests this</p>
                      {!readOnly && (
                        <p className="ev-xsmall-p-dark mb-1">
                          Pick the ones that apply, then use the suggestion.
                        </p>
                      )}
                      {aiAssistSuggestion.reasons.map((reason, idx) => {
                        const isInclude = (reason?.criteria || "").toUpperCase() === "INCLUSION";
                        // Without an id the reason cannot be matched to a
                        // criterion on save, so it stays read-only.
                        const pickable = !readOnly && reason?.id != null;
                        const isPicked = reason?.id != null
                          && selectedAssistReasonIds.includes(reason.id);
                        return (
                          <div
                            key={reason.id || idx}
                            className={`d-flex align-items-center gap-1 mb-1 p-1 ev-radius-xsmall
                              ${isPicked ? "ev-card-6" : ""} ${pickable ? "cursor-pointer" : ""}`}
                            onClick={() => { if (pickable) toggleAssistReason(reason.id); }}
                          >
                            {!readOnly && (
                              <input
                                type="checkbox"
                                className="flex-shrink-0"
                                checked={isPicked}
                                disabled={!pickable}
                                onChange={() => { if (pickable) toggleAssistReason(reason.id); }}
                              />
                            )}
                            <div
                              className={`d-flex align-items-center gap-1 p-1 ev-radius-xsmall ev-bold-med
                                ${isInclude ? "slr-criteria-incl" : "slr-criteria-excl"}`}
                            >
                              <span
                                className="d-flex align-items-center justify-content-center ev-radius-large ev-bold-large"
                                style={{
                                  width: 16,
                                  height: 16,
                                  background: isInclude ? "#4CAF50" : "#E53935",
                                  color: "#fff",
                                  fontSize: 11,
                                  lineHeight: "16px",
                                }}
                              >
                                {isInclude ? "✓" : "✕"}
                              </span>
                              <span className="ev-reg-p-dark">{isInclude ? "Include" : "Exclude"}</span>
                            </div>
                            <textarea
                              rows={1}
                              readOnly
                              value={reason.reason || ""}
                              className="formField3 flex-grow-1 ev-card-8 p-1 ev-radius-xsmall ev-small-p-dark-blue ev-bold-reg"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Confidence + Actions */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-1">
                      {!readOnly && (
                        <button
                          className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                          onClick={handleUseAssistSuggestion}
                          disabled={
                            (aiAssistSuggestion.reasons?.length > 0)
                            && selectedAssistReasonIds.length === 0
                          }
                          title={
                            (aiAssistSuggestion.reasons?.length > 0
                              && selectedAssistReasonIds.length === 0)
                              ? "Select at least one of the reasons above first"
                              : undefined
                          }
                        >
                          Use Suggestion
                        </button>
                      )}
                    </div>
                    {aiAssistSuggestion.confidence_score != null && (
                      <span className="ev-xsmall-p-dark ev-bold-med px-1 py-1 ev-radius-xsmall ev-card-2 ps-1">
                        Confidence: {Math.round(aiAssistSuggestion.confidence_score)}%
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <p className="ev-xsmall-p-dark mb-0">No AI suggestion available.</p>
              )}
            </div>
          )}

          {(conflictMode || isQC) && (
            <div className="ev-card-2 p-2 mb-2">
              <p className="ev-small-p-blue ev-bold-med mb-1">Screenings</p>

              {/* DYNAMIC HUMAN LIST */}
              {humanDecisions.length > 0 &&
                humanDecisions.map((hDecision, idx) => {
                  const isIncluded =
                    String(hDecision?.decision || "").toUpperCase() ===
                    "INCLUDED";
                  // CHECK IF THIS DECISION MATCHES CURRENT FORM STATE
                  const decisionMatches = decision === hDecision.decision;
                  const reasonIds = (hDecision.reasons || []).map((r) =>
                    typeof r === "object" ? r.id : r,
                  );
                  const reasonsMatch =
                    reasonIds.length === selectedReasonIds.length &&
                    reasonIds.every((id) => selectedReasonIds.includes(id));
                  const commentsMatch =
                    (comments || "") === (hDecision.comments || "");

                  const isUsing =
                    decisionMatches && reasonsMatch && commentsMatch;
                  return (
                    <div
                      key={`human-${idx}`}
                      className="d-flex justify-content-between align-items-center p-2 mb-1 ev-radius-xsmall ev-card-8"
                    >
                      <span className="ev-bold-med ps-2 ev-small-p-dark">Human {idx + 1}</span>

                      <div className="d-flex gap-2 align-items-center">
                        <button
                          className="ev-btn-blue-fill-round ev-btn-small"
                          onClick={() => { if (!readOnly) handleUseDecision(hDecision); }}
                          disabled={readOnly}
                        >
                          {isUsing ? (
                            <>
                              Using
                            </>
                          ) : (
                            "Use"
                          )}
                        </button>

                        <div
                          className={`ev-decision-card ${isIncluded ? "included" : "excluded"}`}
                        >
                          <span
                            className={`ev-decision-icon ${isIncluded ? "included" : "excluded"}`}
                          >
                            <i
                              className={`bi ${isIncluded ? "bi-check-lg" : "bi-x-lg"}`}
                            />
                          </span>
                          {isIncluded ? "Include" : "Exclude"}
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* DYNAMIC AI LIST */}
              {aiDecisions.length > 0 &&
                aiDecisions.map((aiItem, idx) => {
                  const isIncluded =
                    String(aiItem?.decision || "").toUpperCase() === "INCLUDED";
                  // CHECK IF THIS AI DECISION MATCHES CURRENT FORM STATE
                  const decisionMatches = decision === aiItem.decision;
                  const reasonIds = (aiItem.reasons || []).map((r) =>
                    typeof r === "object" ? r.id : r
                  );
                  const reasonsMatch =
                    reasonIds.length === selectedReasonIds.length &&
                    reasonIds.every(id => selectedReasonIds.includes(id));
                  const commentsMatch =
                    (comments || "") === (aiItem.summary || aiItem.comments || "");

                  const isUsing = decisionMatches && reasonsMatch && commentsMatch;

                  return (
                    <div
                      key={`ai-${idx}`}
                      className="p-2 mb-1 ev-radius-xsmall ev-card-8"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                      <span
                        className="ev-bold-med ps-2 text-truncate ev-small-p-dark"
                        style={{ maxWidth: "120px" }}
                      >
                        {aiItem.model === "textmodel" ? "claude-sonnet-4-6" : (aiItem.model || `Model ${idx + 1}`)}
                      </span>

                      <div className="d-flex gap-2 align-items-center">
                        <button
                          className="ev-btn-blue-fill-round ev-btn-small"
                          onClick={() => { if (!readOnly) handleUseDecision(aiItem); }}
                          disabled={readOnly}
                        >
                          {isUsing ? (
                            <>
                              Using
                            </>
                          ) : (
                            "Use"
                          )}
                        </button>

                        <div
                          className={`ev-decision-card ${isIncluded ? "included" : "excluded"}`}
                        >
                          <span
                            className={`ev-decision-icon ${isIncluded ? "included" : "excluded"}`}
                          >
                            <i
                              className={`bi ${isIncluded ? "bi-check-lg" : "bi-x-lg"}`}
                            />
                          </span>
                          {isIncluded ? "Include" : "Exclude"}
                        </div>
                      </div>
                      </div>

                      {aiItem.confidence_score != null && (
                        <div className="mt-2 ps-1">
                          <span className="ev-xsmall-p-dark ev-bold-med px-2 py-1 ev-radius-xsmall ev-card-2">
                            Confidence: {aiItem.confidence_score}/100
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

              {/* Fallback */}
              {humanDecisions.length === 0 && aiDecisions.length === 0 && (
                <p className="ev-xsmall-p-dark ps-2">
                  No screening decisions available.
                </p>
              )}
            </div>
          )}

          

          {isDuplicateExclusion ? (
            <div> 
              <p className="ev-small-p-blue ev-bold-med mb-0 pb-1">
            {conflictMode ? "Final Decision" : "Decision"}
          </p>
            <div className="d-flex justify-content-between align-items-center p-2 mb-1 ev-radius-xsmall ev-card-8">
              <div className="ev-alert-card-warn p-1 ev-radius-xsmall px-3 ev-fs-11">Duplicate</div>
              <div className="ev-decision-card excluded">
                <span className="ev-decision-icon excluded">
                  <i className="bi bi-x-lg" />
                </span>
                Exclude
              </div>
            </div>
            </div>
          ) : (
          <div className="position-relative mb-1 px-2 py-0">
            <p className="ev-small-p-blue p-2 ev-bold-med mb-0">
            {conflictMode ? "Final Decision" : "Select Decision"}
          </p>
            <div
              className={`ev-message-card2 d-flex justify-content-between align-items-center 
               px-3 py-2 ev-radius-xsmall ${readOnly ? "" : "cursor-pointer"}`}
              onClick={() => { if (!readOnly) setDecisionOpen((v) => !v); }}
            >
              <span className="ev-bold-med ev-reg-p-dark-blue">
                {decision === "INCLUDED" ? "Include" : "Exclude"}
              </span>
              {!readOnly && (
                <i
                  className={`bi ${decisionOpen ? "bi-chevron-up" : "bi-chevron-down"}`}
                />
              )}
            </div>

            {decisionOpen && !readOnly && (
              <div className="ev-card-4 position-absolute w-100 mt-1 ev-radius-xsmall z-1">
                {["INCLUDED", "EXCLUDED"].map((opt) => (
                  <div
                    key={opt}
                    className={`px-3 py-2 cursor-pointer ev-small-p-dark ev-radius-xsmall
            ${decision === opt ? "ev-card-2" : ""}`}
                    onClick={() => {
                      handleDecision(opt);
                      setDecisionOpen(false);
                    }}
                  >
                    {opt === "INCLUDED" ? "Include" : "Exclude"}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {!isDuplicateExclusion && (
          <div className="ev-card-2 px-2 py-0 mb-1">
            <hr />
            <p className="ev-small-p-blue ev-bold-med mb-1">Select Reasons</p>

            {criteriaList.length > 0 &&
              criteriaList.map((reason) => (
                <ReasonRow
                  key={reason.id}
                  reason={reason}
                  checked={selectedReasonIds.includes(reason.id)}
                  onToggle={() => toggleReason(reason.id)}
                  disabled={readOnly}
                />
              ))}

            {manualReasons.length > 0 && (
              <div className="mt-2">
                <p className="ev-xsmall-p-dark ev-bold-med mb-1">
                  Manual reasons <span className="ev-xsmall-p-dark ev-bold-reg">(entered outside the standard criteria list)</span>
                </p>
                {manualReasons.map((m) => {
                  const isInclude = decision === "INCLUDED";
                  return (
                    <div
                      key={`manual-${m.id}`}
                      className="d-flex align-items-center gap-1 mb-1"
                    >
                      {/* Checkbox — always checked/disabled: manual reasons are
                          always part of the saved decision, there's nothing to toggle */}
                      <input
                        type="checkbox"
                        checked
                        disabled
                        className="w-5"
                      />

                      <div
                        className={`d-flex align-items-center gap-1 px-2 py-1 ev-radius-xsmall ev-bold-med
                          ${isInclude ? "slr-criteria-incl" : "slr-criteria-excl"}`}
                        style={{ minWidth: 90, justifyContent: "center" }}
                      >
                        <span
                          className="d-flex align-items-center justify-content-center ev-radius-large ev-bold-large"
                          style={{
                            width: 16,
                            height: 16,
                            background: isInclude ? "#4CAF50" : "#E53935",
                            color: "#fff",
                            fontSize: 11,
                          }}
                        >
                          {isInclude ? "✓" : "✕"}
                        </span>
                        <span className="ev-reg-p-dark">{isInclude ? "Include" : "Exclude"}</span>
                      </div>

                      <textarea
                        rows={1}
                        readOnly
                        value={m.reason_text}
                        className="formField3 flex-grow-1 ev-card-8 p-1 ev-radius-xsmall ev-small-p-dark-blue ev-bold-reg"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          <hr />

          <div className="ev-card-2 p-2 mb-1">
            <p className="ev-small-p-blue ev-bold-med mb-1">Comments</p>

            <textarea
              className="w-100 ev-message-card6 formField3 ev-radius-xsmall"
              rows={4}
              placeholder="Add your comments here..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={readOnly}
              style={{ resize: "vertical" }}
            />
          </div>

          <div className="ev-card-2 p-2 mb-1">
            <p className="ev-small-p-blue ev-bold-med mb-1">Tags</p>

            <div className="d-flex flex-wrap gap-1">
              {matchingTags.length > 0 ? (
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {matchingTags.map((tag, i) => (
                    <PicoTag
                      key={i}
                      text={tag}
                      picoJson={literature?.pico_json}
                    />
                  ))}
                </div>
              ) : (
                <span className="ev-xsmall-p-dark">
                  No matching tags found for this paper
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenReviewPanel;
